import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { CoverImage } from '@/components/CoverImage'
import { CheckCircle2, AlertTriangle, Globe, Smartphone, Pencil, Heart, Search, ArrowRight } from 'lucide-react'

const platforms = [
  {
    name: 'WordPress (self-hosted)',
    good: 'Flexible, huge plugin ecosystem, strong for SEO and archives',
    watch: 'Needs ongoing maintenance, security patches, someone technical on staff',
    best: 'Festivals with a technical staff member or agency relationship',
    rating: 4,
  },
  {
    name: 'Squarespace',
    good: 'Beautiful templates, easy for non-technical staff to update',
    watch: 'Limited ticketing, awkward third-party embeds, walls at scale',
    best: 'Smaller festivals with general admission and modest budgets',
    rating: 3,
  },
  {
    name: 'Webflow',
    good: 'Full design control, solid hosting, no maintenance overhead',
    watch: 'Steep learning curve, requires a designer, more expensive',
    best: 'Festivals with a specific visual brand and a design partner',
    rating: 4,
  },
  {
    name: 'Wix',
    good: 'Quick setup, affordable, Wix Bookings for simple registration',
    watch: 'Feels underpowered for complex ticketing and donor management',
    best: 'Community music schools and very small festivals',
    rating: 2,
  },
  {
    name: 'Custom / Headless CMS',
    good: 'Maximum control and performance, tailored to exact needs',
    watch: 'Requires ongoing developer relationship and meaningful budget',
    best: 'Major festivals (6+ weeks, 40+ events, national donor base)',
    rating: 5,
  },
]

const priorities = [
  {
    icon: Globe,
    title: 'Ticketing Integration',
    description:
      'Does the purchase flow feel native, or is it a jarring redirect to a third-party page with different fonts and colors? Every visual break in the purchase path reduces conversion.',
  },
  {
    icon: Smartphone,
    title: 'Mobile Experience',
    description:
      "Pull up your current site on your phone right now. If the event calendar is hard to navigate with a thumb, or if the ticket button is buried, that's a problem.",
  },
  {
    icon: Pencil,
    title: 'Content Update Speed',
    description:
      'Artists cancel. Times shift. Programs get added. The platform has to make those updates fast and easy for whoever owns the site, not just a developer.',
  },
  {
    icon: Heart,
    title: 'Donation Pages',
    description:
      "A donation page that's hard to find or visually disconnected from the rest of the site signals to a prospective donor that online giving is an afterthought.",
  },
  {
    icon: Search,
    title: 'Search Visibility',
    description:
      "Individual event pages need proper titles, descriptions, and structured data so Google can index them. If your concert doesn't show up when someone searches for it by name, the platform is working against you.",
  },
]

const mistakes = [
  {
    mistake: 'No event calendar that Google can read',
    detail:
      'A PDF of the schedule does not serve search. Each concert should have its own page with date, title, venue, and artists.',
  },
  {
    mistake: 'Ticketing flow that leaves the site entirely',
    detail:
      'Even if you must use a third-party vendor, the transition should be seamless. Sudden jumps to an unstyled vendor page erode trust.',
  },
  {
    mistake: 'Bios and program notes that vanish after the season',
    detail: 'Archiving past seasons builds long-term search authority and serves press and donor research.',
  },
  {
    mistake: 'No clear path for first-time visitors',
    detail: 'A first-time patron and a longtime subscriber have different needs. Good site architecture serves both.',
  },
  {
    mistake: 'Donor and sponsor pages treated as afterthoughts',
    detail:
      'These are often the pages most scrutinized by high-value visitors. They should be easy to find and professionally presented.',
  },
]

function RatingDots({ rating }: { rating: number }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`h-2 w-2 rounded-full ${i <= rating ? 'bg-primary' : 'bg-border'}`} />
      ))}
    </div>
  )
}

export default function BestWebsitePlatformsPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/best-website-platforms-for-music-festivals', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: 'Best Website Platforms for Music Festivals',
    description:
      "Choosing the right music festival website platform affects ticket sales, donor trust, and search visibility. Here's how to pick the one that fits your festival.",
    url: '/blog/best-website-platforms-for-music-festivals',
    canonical: '/blog/best-website-platforms-for-music-festivals',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2026-04-16',
      author: 'Garrett John Law',
      section: 'Guide',
      tags: ['music festival', 'website platform', 'ticketing', 'digital strategy'],
    },
    schema: createArticleSchema({
      headline: 'Best Website Platforms for Music Festivals',
      description:
        'Choosing the right music festival website platform affects ticket sales, donor trust, and search visibility.',
      datePublished: '2026-04-16',
      url: '/blog/best-website-platforms-for-music-festivals',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h1">Best Website Platforms for Music Festivals</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2026-04-16">April 16, 2026</time>
          <span>&middot;</span>
          <span>10 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          Choosing the right platform affects ticket sales, donor trust, and search visibility. Here&apos;s how to pick
          the one that fits your festival.
        </p>
      </header>

      <CoverImage slug="best-website-platforms-for-music-festivals" alt="A music festival crowd at an outdoor stage" />

      <Separator />

      {/* Introduction */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <p className="text-lg">
          Choosing a <strong>music festival website platform</strong> is one of the most consequential decisions a
          festival director makes, and it rarely gets the attention it deserves.
        </p>
        <p>
          Most festivals end up on whatever platform the board&apos;s tech-savvy volunteer set up years ago, or whatever
          the ticketing vendor happened to bundle in. Then summer arrives, a soprano cancels, the schedule changes, and
          someone is trying to update a WordPress page they&apos;ve never touched while also fielding calls from the
          caterer.
        </p>
        <p>
          Sound familiar? This guide walks through the real options, what actually matters for classical and
          contemporary music festivals, and how to think through the decision before your next season builds momentum.
        </p>
      </section>

      <Separator />

      {/* Why It Matters */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">Why Platform Choice Matters More Than You Think</h2>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-muted-foreground">
            The website is the <strong className="text-foreground">operational center</strong> of a festival, not just a
            brochure. Patrons check it for parking the morning of a concert. Donors look at it before they write a
            check. Press contacts pull artist bios from it. School groups try to book tickets through it.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="border-border rounded-lg border p-4 text-center">
            <p className="text-primary mb-1 text-2xl font-bold">Ticket Sales</p>
            <p className="text-muted-foreground text-sm">
              A clunky ticketing integration costs you sales every single day tickets are live
            </p>
          </div>
          <div className="border-border rounded-lg border p-4 text-center">
            <p className="text-primary mb-1 text-2xl font-bold">Donor Trust</p>
            <p className="text-muted-foreground text-sm">
              Donors evaluate credibility before writing a check. Your site is their first impression
            </p>
          </div>
          <div className="border-border rounded-lg border p-4 text-center">
            <p className="text-primary mb-1 text-2xl font-bold">Search Visibility</p>
            <p className="text-muted-foreground text-sm">
              A slow site with poor structure is invisible to &ldquo;summer orchestra concerts near me&rdquo;
            </p>
          </div>
        </div>

        <p className="text-muted-foreground">
          For a summer institute with both a concert series and student enrollment, a platform that can&apos;t handle
          two distinct audience types forces you into awkward workarounds &mdash; like a separate Squarespace site for
          concerts and a Google Form for applications, neither of which looks credible.
        </p>
      </section>

      <Separator />

      {/* Platform Comparison */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">The Main Options, Honestly</h2>
        <p className="text-muted-foreground">
          Here&apos;s an honest look at the platforms festivals actually use, and what each one is good at.
        </p>

        <div className="space-y-4">
          {platforms.map((platform) => (
            <motion.div
              key={platform.name}
              className="border-border space-y-3 rounded-lg border p-6"
              whileHover={{ y: -2 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold">{platform.name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">Flexibility</span>
                  <RatingDots rating={platform.rating} />
                </div>
              </div>
              <div className="grid gap-4 text-sm md:grid-cols-2">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{platform.good}</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                  <span className="text-muted-foreground">{platform.watch}</span>
                </div>
              </div>
              <p className="text-muted-foreground border-border border-t pt-2 text-xs">
                <strong className="text-foreground">Best for:</strong> {platform.best}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <Separator />

      {/* What to Prioritize */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">What to Prioritize When Evaluating</h2>
        <p className="text-muted-foreground">
          Before you get drawn into comparing templates or monthly pricing, get clear on what your festival actually
          needs a website to do.
        </p>

        <div className="space-y-4">
          {priorities.map((item) => (
            <div key={item.title} className="bg-muted/5 border-border flex items-start gap-4 rounded-lg border p-5">
              <item.icon className="text-primary mt-0.5 h-6 w-6 flex-shrink-0" />
              <div>
                <h3 className="mb-1 font-semibold">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* Common Mistakes */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">Common Mistakes Festivals Make</h2>
        <p className="text-muted-foreground">
          Having looked at a lot of festival sites, the same problems come up repeatedly. These aren&apos;t
          platform-specific &mdash; they&apos;re decisions that happen regardless of which tool the site is built on.
        </p>

        <div className="space-y-3">
          {mistakes.map((item) => (
            <div key={item.mistake} className="border-destructive/50 flex items-start gap-3 border-l-4 py-2 pl-4">
              <div>
                <p className="text-destructive font-semibold">{item.mistake}</p>
                <p className="text-muted-foreground mt-1 text-sm">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* Music Schools */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">If You Run Both a Festival and a Music School</h2>

        <div className="bg-primary/5 border-primary/20 space-y-4 rounded-lg border p-6">
          <p className="text-muted-foreground">
            You&apos;re serving at least <strong className="text-foreground">two distinct audiences</strong> with
            different questions, timelines, and trust signals:
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border-border rounded-lg border p-4">
              <p className="mb-2 font-semibold">Concertgoers Want</p>
              <ul className="text-muted-foreground space-y-1 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Clean calendar
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Clear venue information
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Fast checkout
                </li>
              </ul>
            </div>
            <div className="border-border rounded-lg border p-4">
              <p className="mb-2 font-semibold">Prospective Students Want</p>
              <ul className="text-muted-foreground space-y-1 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Faculty credentials
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Tuition and financial aid
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 h-3 w-3 flex-shrink-0" />
                  Alumni outcomes
                </li>
              </ul>
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Platforms like WordPress and Webflow handle multi-section architecture well. Squarespace can work with
            careful planning. The risk is trying to force both use cases into a template designed for a simpler
            single-purpose site.
          </p>
        </div>
      </section>

      <Separator />

      {/* Making the Decision */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">Making the Decision</h2>

        <div className="bg-muted/5 border-border space-y-4 rounded-lg border p-6">
          <p className="text-lg font-medium">The honest framework:</p>
          <ol className="space-y-3">
            {[
              'Start with your operational needs, not with what looks good in a demo',
              'Map out the actual jobs the website has to do during your festival season',
              'Match those jobs to platform capabilities',
              'Consider who will maintain the site between seasons and whether they have the skills',
            ].map((step, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold">
                  {idx + 1}
                </span>
                <span className="text-muted-foreground">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-muted-foreground">
            <strong className="text-foreground">The bottom line:</strong> A beautiful site on a platform nobody on your
            staff can update is a liability. A simpler site that your communications director can manage confidently,
            that loads fast on a phone, and that connects cleanly to your ticketing system will outperform it every
            season.
          </p>
        </div>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Not Sure Which Platform Is Right for You?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We build websites and digital systems for classical music festivals. Schedule a free 15-minute call and
          we&apos;ll review your current setup and recommend the best path forward.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <Button variant="default" size="lg" className="cursor-pointer">
                Book a Free Call <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/services/festival-websites">
              <Button variant="outline" size="lg" className="cursor-pointer">
                See Our Festival Packages
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Separator />

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {['music festival', 'website platform', 'ticketing', 'digital strategy'].map((tag) => (
          <span key={tag} className="bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs">
            {tag}
          </span>
        ))}
      </div>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a musician and software engineer who builds digital systems for
              classical music festivals. An Interlochen alum and CIM graduate, he currently serves as organist and
              choirmaster at Holy Trinity Episcopal Church in Los Angeles.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn more about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
